import type { EventBus } from "../core/EventBus.ts";
import { FEEL_BEATS, type FeelBeat } from "../core/feelBudget.ts";
import { enemyVisualRadius } from "../rendering/EnemyVisualScale.ts";

/**
 * BL-237 (WOW Pass 3) — the HERO-MOMENT director, render/feel side only.
 *
 * The kill-juice loop ({@link setupCombatFeel}) lights up the *frequent* loop
 * (per-kill pop / combo punch). This is its sibling for the *rare, big* moments
 * the arena was leaving SILENT: the boss arriving, the boss dying, a tower
 * reaching GOLD, the mainframe going critical. It composes the existing
 * render-side effectors — ScreenEffects (shake · flash · vignette · the BL-238
 * camera punch) + GameLoop hit-stop + the particle burst — into one named
 * "impact beat" per moment, fired off EXISTING events.
 *
 * Holistic + modular: the {@link BEATS} table is the ONE place each big moment's
 * in-arena impact is tuned; the effectors are injected (DI) so it unit-tests
 * with spies (mirrors `CombatFeelAudio`). It is the VISUAL-impact layer — audio
 * is owned by AudioEngine and haptics by nativeShell, both already mapped off
 * the same events, so this deliberately does NOT re-fire either (no double-up).
 *
 * It NEVER touches the sim / RNG / balance — it only reads event payloads and
 * drives render-side systems, and (like CombatFeel) it is wired ONLY in the live
 * app, never in the golden-master harness → the masters stay byte-identical.
 * Every channel self-gates accessibility: shake + punch damp to nothing under
 * reduced motion / perf load (arenaMotionScale), hit-stop is skipped under
 * reduced motion (motionScale), and flash routes through the FlashGovernor +
 * the red-flash photosensitivity carve-out.
 */

type Tier = "micro" | "short" | "payload";

/**
 * S195 / BL-279 — the hero-beat budgets moved to THE central tier table
 * (`core/feelBudget.ts`, canon: docs/design/COMBAT_FEEL_DIRECTION.md). This
 * module keeps the wiring (which event plays which beat, with which guards);
 * the table owns every number. Boss-kill is now punch-led per anti-fight law
 * #4 (shake 2.6 px support, was 3.5 — the camera punch is the hero), and the
 * boss flashes carry the rare-canon `priority` flag (they bypass the
 * governor's rate window, never its stack cap).
 */
const BEATS: Record<string, FeelBeat> = {
  bossArrival: FEEL_BEATS.bossArrival,
  bossDefeat: FEEL_BEATS.bossKill,
  goldReached: FEEL_BEATS.goldReached,
  mainframeCritical: FEEL_BEATS.mainframeCritical,
};

// DI "slice" interfaces use property-arrow (this: void) signatures, not method
// shorthand — the effectors are injected functions, and this keeps spy refs
// (`expect(spy.shake)`) free of the unbound-method warning. The real classes'
// methods remain assignable to these.

/** Minimal ScreenEffects slice the director drives (DI for spies). */
export interface MomentScreenEffects {
  shake: (intensity: number, duration: number) => void;
  flash: (
    color: string,
    alpha: number,
    opts?: { priority?: boolean },
  ) => void;
  vignette: (color: string, duration: number) => void;
  punch: (scale: number, durationMs: number) => void;
  chromaPulse: (peak: number, durationMs: number) => void;
}

/** Minimal GameLoop slice (the determinism-neutral freeze). */
export interface MomentGameLoop {
  hitStopTier: (tier: Tier) => void;
}

/** Minimal ParticleSystem slice for the boss/gold bursts (optional). */
export interface MomentParticles {
  emitRing: (
    x: number,
    y: number,
    radius: number,
    element: string,
    count?: number,
  ) => void;
  emitKill: (x: number, y: number, element: string) => void;
}

export interface MomentDirectorDeps {
  eventBus: EventBus;
  screenEffects: MomentScreenEffects;
  gameLoop: MomentGameLoop;
  particles?: MomentParticles | null;
}

/** Lives below which a mainframe hit escalates to the critical danger beat. */
const CRITICAL_LIVES = 2;
/** Particle element key for the boss-defeat burst (electric, dramatic; emit()
 *  falls back to "fire" for unknown keys, so this is always safe). */
const BOSS_BURST_ELEMENT = "lightning";

/**
 * Wire the hero-moment beats. Returns a teardown that removes every listener
 * (mirrors {@link setupCombatFeel}). Render/feel-only ⇒ sim byte-identical.
 */
export function setupMomentDirector(deps: MomentDirectorDeps): () => void {
  const { eventBus, screenEffects, gameLoop, particles } = deps;
  const unsubs: (() => void)[] = [];

  const play = (beat: FeelBeat): void => {
    if (beat.punch) screenEffects.punch(beat.punch[0], beat.punch[1]);
    if (beat.chroma) screenEffects.chromaPulse(beat.chroma[0], beat.chroma[1]);
    if (beat.shake) screenEffects.shake(beat.shake[0], beat.shake[1]);
    if (beat.flash) {
      screenEffects.flash(
        beat.flash[0],
        beat.flash[1],
        beat.flashPriority ? { priority: true } : {},
      );
    }
    if (beat.vignette) screenEffects.vignette(beat.vignette[0], beat.vignette[1]);
    if (beat.hitStop) gameLoop.hitStopTier(beat.hitStop);
  };

  // Boss arrival — first sight of the boss reads as an event.
  unsubs.push(eventBus.on("boss.spawned", () => play(BEATS.bossArrival!)));

  // Boss defeat — the marquee beat: punch + freeze + burst at the boss site.
  unsubs.push(
    eventBus.on("boss.defeated", (d: unknown) => {
      play(BEATS.bossDefeat!);
      const boss = (d as { boss?: { x: number; y: number; radius?: number } })
        .boss;
      if (particles && boss) {
        // BL-285 — frame the ring on the radius the boss is DRAWN at, not the
        // authored one; otherwise the burst now fires inside the body.
        const r = enemyVisualRadius(
          boss.radius && boss.radius > 0 ? boss.radius : 16,
        );
        particles.emitRing(boss.x, boss.y, r * 1.5, BOSS_BURST_ELEMENT, 24);
        particles.emitKill(boss.x, boss.y, BOSS_BURST_ELEMENT);
      }
    }),
  );

  // A tower reaching GOLD (L5→L6) — fires exactly once (GOLD is the cap).
  unsubs.push(
    eventBus.on("tower.upgraded", (d: unknown) => {
      const tower = (
        d as {
          tower?: {
            isGold?: () => boolean;
            x: number;
            y: number;
            element?: string;
          };
        }
      ).tower;
      if (!tower || !tower.isGold || !tower.isGold()) return;
      play(BEATS.goldReached!);
      if (particles) {
        particles.emitRing(tower.x, tower.y, 22, tower.element ?? "fire", 20);
      }
    }),
  );

  // Mainframe critical — escalate the danger feel at ≤2 lives (on top of the
  // existing per-hit flash/vignette + sustained tint wired in main.ts).
  unsubs.push(
    eventBus.on("mainframe.hit", (d: unknown) => {
      const lives = (d as { livesLeft?: number }).livesLeft ?? Number.MAX_SAFE_INTEGER;
      if (lives > 0 && lives <= CRITICAL_LIVES) play(BEATS.mainframeCritical!);
    }),
  );

  return () => {
    for (const unsub of unsubs) unsub();
  };
}
